import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import PillarBadge from "@/components/PillarBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [kyc, setKyc] = useState([]);

  const load = () => {
    api.get("/admin/pending").then((r) => setPending(r.data));
    api.get("/admin/kyc/pending").then((r) => setKyc(r.data));
  };

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    load();
  }, [user, loading, navigate]);

  const approve = async (id) => {
    await api.post(`/admin/verify/${id}`);
    toast.success("Experience verified");
    load();
  };

  const kycDecision = async (host_id, status) => {
    await api.post(`/admin/kyc/${host_id}`, { status });
    toast.success(`KYC ${status}`);
    load();
  };

  if (!user) return null;
  return (
    <div className="min-h-screen bg-[#F4F2EC]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="overline">Admin</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-1 mb-8">Pending verification</h1>
        {pending.length === 0 ? (
          <div className="bg-white border border-subtle rounded-2xl p-10 text-center text-muted2" data-testid="no-pending">
            All listings are up to date.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {pending.map((e) => (
              <div key={e.id} className="bg-white border border-subtle rounded-2xl overflow-hidden" data-testid={`pending-${e.id}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={e.images?.[0]} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="flex flex-wrap gap-1 mb-2">{e.pillars?.map(p => <PillarBadge key={p} pillar={p} />)}</div>
                  <div className="font-serif text-xl">{e.title}</div>
                  <div className="text-xs text-muted2 mt-1">{e.location} · Host: {e.host_name}</div>
                  <p className="text-sm text-muted2 mt-2 line-clamp-3">{e.description}</p>
                  <Button data-testid={`approve-${e.id}`} onClick={() => approve(e.id)} className="w-full mt-4 rounded-full bg-moss hover:bg-moss-hover text-canvas">
                    Approve & verify
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <h2 className="font-serif text-3xl mt-14 mb-6">Pending KYC</h2>
        {kyc.length === 0 ? (
          <div className="bg-white border border-subtle rounded-2xl p-10 text-center text-muted2" data-testid="no-kyc">No KYC submissions to review.</div>
        ) : (
          <div className="space-y-3">
            {kyc.map((k) => (
              <div key={k.host_id} className="bg-white border border-subtle rounded-2xl p-5 flex flex-wrap justify-between items-center gap-4" data-testid={`kyc-${k.host_id}`}>
                <div className="text-sm">
                  <div className="font-serif text-xl">{k.host_name}</div>
                  <div className="text-xs text-muted2">{k.host_email} · {k.country || "—"}</div>
                  <div className="text-xs text-muted2 mt-1">Bank: {k.bank_name} · A/C ••••{k.account_number_last4} · Tax {k.tax_id}</div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`kyc-approve-${k.host_id}`} onClick={() => kycDecision(k.host_id, "approved")} className="rounded-full bg-moss hover:bg-moss-hover text-canvas text-sm">Approve</Button>
                  <Button data-testid={`kyc-reject-${k.host_id}`} onClick={() => kycDecision(k.host_id, "rejected")} variant="outline" className="rounded-full">Reject</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
